import { useToast } from '@chakra-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import { AppDispatch } from 'config/redux'
import { useDispatch, useSelector } from 'react-redux'
import { FormContext, useForm } from 'react-hook-form'
import { createJob, getJobActionReference, ICreateJob, Job, updateJob } from 'redux/actions/admin'
import BaseModalV2 from 'components/TasksV2/UI/BaseModalV2'
import { ModalHeader } from '../commonUI/Modal/ModalHeader'
import { ModalFooter } from '../commonUI/Modal/ModalFooter'
import { JobForm } from './Form/JobForm'
import dayjs from 'dayjs'
import { JobFrequencyType, SERVICE_QUEUE_MAPPING } from './utils'
import { RootState } from 'constants/interfaces'
import { getPopData } from 'redux/actions/pops'
import { transformServerDataToPopFormValues } from 'components/Pop/types'
import useLocaleText from 'components/useLocaleText'
import keys from 'constants/keys'
import { ActionParamsProcessorManager } from './ActionParamsProcessors'

export interface ICreateJobForm extends Omit<ICreateJob, 'job_data'> {
  job_data: Omit<ICreateJob['job_data'], 'schedule_start_ts' | 'schedule_end_ts'> & {
    schedule_start_ts: Date
    schedule_end_ts: Date
  }
}

const START_DATE = dayjs(new Date())
const END_DATE = dayjs(new Date()).add(7, 'day')

const DEFAULT_VALUES: ICreateJobForm = {
  is_recurring: false,
  job_data: {
    action_object_id: null,
    action_params: null,
    action_queue_name: SERVICE_QUEUE_MAPPING[keys.JOBS_SERVICES.POPS],
    action_service: '',
    initiator: 1,
    rid: '',
    schedule_start_ts: START_DATE.toDate(),
    schedule_end_ts: END_DATE.toDate(),
    title: '',
    content: {
      text: '',
    },
    recurrence_frequency: JobFrequencyType.ONCE,
    recurrence_interval: 1,
    recurrence_days_of_week: [],
  },
}

const transformJobToICreateJobForm = (job: Job): ICreateJobForm => {
  return {
    is_recurring: job.recurrence_frequency !== null && job.recurrence_frequency !== JobFrequencyType.ONCE,
    job_data: {
      action_object_id: job.action_object_id || null,
      action_params: job.action_params as any,
      action_queue_name: SERVICE_QUEUE_MAPPING[job.action_service],
      action_service: job.action_service,
      initiator: job.initiator,
      rid: job.rid,
      schedule_start_ts: dayjs.utc(job.schedule_start_ts, 'YYYYMMDDHHmmss').local().toDate(),
      schedule_end_ts: job.schedule_end_ts
        ? dayjs.utc(job.schedule_end_ts, 'YYYYMMDDHHmmss').local().toDate()
        : END_DATE.toDate(),
      title: job.title,
      content: job.content,
      recurrence_frequency: job.recurrence_frequency,
      recurrence_interval: job.recurrence_interval,
      recurrence_days_of_week: job.recurrence_days_of_week,
    },
  }
}

interface JobModalProps {
  isOpen: boolean
  onClose: () => void
  requestCloseTagModal: () => void
  job?: Job | null
}

export const JobModal: React.FC<JobModalProps> = ({ isOpen, onClose, job, requestCloseTagModal }) => {
  const toast = useToast()
  const dispatch: AppDispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const { rid } = useSelector((state: RootState) => state.config.config)

  const methods = useForm<ICreateJobForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: job ? transformJobToICreateJobForm(job) : DEFAULT_VALUES,
  })

  const onSubmit = async (data: ICreateJobForm) => {
    try {
      setIsLoading(true)

      const updatedData: ICreateJob = {
        ...data,
        job_data: {
          ...data.job_data,
          schedule_start_ts: dayjs(data.job_data.schedule_start_ts).format('YYYYMMDDHHmmss'),
          schedule_end_ts:
            data.job_data.recurrence_frequency !== JobFrequencyType.ONCE
              ? dayjs(data.job_data.schedule_end_ts).format('YYYYMMDDHHmmss')
              : null,
          action_params: ActionParamsProcessorManager.processActionParams(
            data.job_data.action_service,
            data.job_data.action_params,
            rid
          ),
        },
      }

      if (
        updatedData.job_data.action_params &&
        typeof updatedData.job_data.action_params === 'object' &&
        'is_scheduled' in updatedData.job_data.action_params
      ) {
        delete (updatedData.job_data.action_params as any).is_scheduled
      }

      if (updatedData.job_data.recurrence_frequency === JobFrequencyType.ONCE) {
        updatedData.job_data.recurrence_frequency = null
      }

      const res = job ? await dispatch(updateJob(job.job_id, updatedData)) : await dispatch(createJob(updatedData))

      if (res) {
        toast({
          title: job ? 'Job updated successfully' : 'Job created successfully',
          status: 'success',
          duration: 3000,
          position: 'top-right',
        })
        onClose()
      }
    } catch (error) {
      console.error(error)
      toast({
        title: 'Failed to create job',
        status: 'error',
        duration: 3000,
        position: 'top-right',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const t_edit_job = useLocaleText('edit_job', true)
  const t_create_job = useLocaleText('create_job', true)
  const t_update_job = useLocaleText('update_job', true)

  const modal_title = job ? t_edit_job : t_create_job
  const admin_submit_button = job ? t_update_job : t_create_job

  const handleGetServiceParams = useCallback(async () => {
    if (job?.action_reference && job?.action_service) {
      try {
        setIsLoading(true)

        // Architecture for supporting different services
        switch (job.action_service) {
          case keys.JOBS_SERVICES.POPS: {
            const { action_params } = await dispatch(getJobActionReference(job.action_reference))
            const popData = await dispatch(getPopData({ pop_id: action_params.pop_id }))
            let transformedData = transformServerDataToPopFormValues(popData)
            methods.setValue('job_data.action_params', transformedData)
            break
          }
          case keys.JOBS_SERVICES.COMPAIGN: {
            const { action_params } = await dispatch(getJobActionReference(job.action_reference))
            // For campaigns, we use action_params as is
            methods.setValue('job_data.action_params', action_params)
            break
          }
          default:
            console.warn(`Service ${job.action_service} is not supported`)
        }
      } catch (error) {
        console.error('Error in handleGetServiceParams', error)
      } finally {
        setIsLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, job])

  useEffect(() => {
    if (job) {
      handleGetServiceParams()
    }
  }, [job, handleGetServiceParams])

  return (
    <FormContext {...methods}>
      <BaseModalV2
        isOpen={isOpen}
        onClose={onClose}
        header={<ModalHeader title={modal_title} onClose={requestCloseTagModal} />}
        body={<JobForm job={job} />}
        footer={<ModalFooter isLoading={isLoading} onSubmit={onSubmit} buttonText={admin_submit_button} />}
        modalContentProps={{
          w: '100%',
          maxW: { base: '90vw', md: '80vw', lg: '800px' },
          minW: '350px',
          pointerEvents: isLoading ? 'none' : 'auto',
        }}
        modalBodyProps={{
          opacity: isLoading ? 0.8 : 1,
          height: 'auto',
          maxH: { base: '80dvh', md: '70dvh' },
        }}
        modalHeaderProps={{ boxShadow: 'none' }}
      />
    </FormContext>
  )
}
